import { useEffect, useState } from "react";
import { ChangeEvent, FormEvent, IQueue, IWorkTime } from "../../../globals/interfaces";
import apiClient from "../../../apiClient";
import { useAppContext } from "../../../context";
import moment from "moment";
import styled from "styled-components";
import { Label } from "reactstrap";
import { Input } from "reactstrap";
import VirtualTable from "../../../components/custom/VirtualTable";
import { formatDatetime, formatEmail, formatSecondsAsMinutes } from "../../../utils";
import Card from "../../../components/custom/Card";
import _ from "lodash";
import Layout from "../../../components/custom/Layout";

type WorkTimeMapItem = {
    email: string;
    duration: number;
    items: Array<IWorkTime>;
};

export type WorkTimeMap = Record<string, WorkTimeMapItem>;

export default function EmployeeProductivity() {

    const { user } = useAppContext();
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [data, setData] = useState<Array<IWorkTime>>([]);
    const [dataMap, setDataMap] = useState<WorkTimeMap>({});
    const [selectedItem, setSelectedItem] = useState<WorkTimeMapItem>();
    const [awbs, setAwbs] = useState<Array<IQueue>>([]);

    useEffect(() => {
        const getData = async () => {
            try {
                const res = await apiClient.get(`/employee-productivity?unit=${user.s_unit}&date=${date}`);
                if (res.status === 200) {
                    const data: Array<IWorkTime> = res.data;
                    const useData = data.map((item) => ({ ...item, durationFormatted: formatSecondsAsMinutes(item.duration) }))
                    setData(useData);
                    const map: WorkTimeMap = {};
                    for (const item of data) {
                        if (!map[item.email]) {
                            map[item.email] = {
                                email: item.email,
                                duration: 0,
                                items: []
                            };
                        }
                        map[item.email].duration += item.duration;
                        map[item.email].items.push(item);
                    }
                    setDataMap(map);
                }
            } catch (err) { }
        }
        getData();
    }, [user.s_unit, date]);

    const tableData = Object.keys(dataMap).map((key) => {
        return {
            email: key,
            duration: formatSecondsAsMinutes(dataMap[key].duration),
            items: dataMap[key].items
        }
    });

    const handleSelectItem = async (item: WorkTimeMapItem) => {
        setSelectedItem(item);
        try {
            const res = await apiClient.get(`/employee-productivity-awbs?unit=${user.s_unit}&date=${date}&email=${item.email}`);
            setAwbs(res.data);
        } catch (err) { }
    }

    return (
        <Layout>
            <Container>
                <h1>Employee Productivity</h1>
                <DateContainer>
                    <Label>Select Date:</Label>
                    <Input
                        type={'date'}
                        value={date}
                        onChange={(e: ChangeEvent) => setDate(e.target.value)}
                        style={{
                            width: '150px'
                        }}
                    />
                </DateContainer>
                <FlexContainer>
                    <VirtualTable
                        data={tableData}
                        mapping={[{
                            name: 'Employee',
                            value: 'email'
                        }, {
                            name: 'Duration (hh:mm:ss)',
                            value: 'duration',
                            number: true
                        }]}
                        handleClick={(item) => handleSelectItem(item)}
                        numRows={10}
                    />
                    {
                        selectedItem !== undefined && (
                            <FlexContainer>
                                <RightContainer>
                                    <h6>Selected Employee: {formatEmail(selectedItem.email)} | Total Time: {selectedItem.duration} | {selectedItem.items.length}</h6>
                                    {
                                        _.get(selectedItem, 'items', []).map((item, i) => (
                                            <CustomCard key={i} className={'mb-2'}>
                                                <FlexContainer>
                                                    <FlexDiv>
                                                        <p>Start</p>
                                                        <p>{formatDatetime(item.startTime)}</p>
                                                    </FlexDiv>
                                                    <FlexDiv>
                                                        <p>End</p>
                                                        <p>{formatDatetime(item.endTime)}</p>
                                                    </FlexDiv>
                                                    <FlexDiv>
                                                        <p>Duration</p>
                                                        <p>{formatSecondsAsMinutes(item.duration)}</p>
                                                    </FlexDiv>
                                                </FlexContainer>
                                            </CustomCard>
                                        ))
                                    }
                                </RightContainer>
                                <RightContainer>
                                    <h6>Total MAWBs processed - {awbs.length}</h6>
                                    <VirtualTable 
                                        data={awbs}
                                        mapping={[{
                                            name: 'Type',
                                            value: 's_type'
                                        }, {
                                            name: 'Status',
                                            value: 's_status'
                                        }, {
                                            name: 'MAWB',
                                            value: 's_mawb'
                                        }, {
                                            name: 'HAWB',
                                            value: 's_hawb'
                                        }]}
                                        numRows={10}
                                    />
                                </RightContainer>
                            </FlexContainer>
                        )
                    }
                </FlexContainer>
            </Container>
        </Layout>
    );
}

const Container = styled.div`
    padding: 10px;
`;

const DateContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 25px;
`;

const RightContainer = styled.div`
    flex: 1;
    min-width: 250px;
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    flex: 1;
    flex-wrap: wrap;
`;

const FlexDiv = styled.div`
    flex: 1;

    & p {
        margin-bottom: 5px;
    }

    & p:first-child {
        font-weight: bold;
    }
`;

const CustomCard = styled(Card)`
    max-width: 700px;
`;